import * as React from 'react'
// views
import {
  Sheet,
  Title,
  Hint,
  SectionLabel,
  Table,
  Td,
  TdArea,
  TheadTd,
  FieldRow,
  FieldLabel,
  Input,
  InlineInput,
  TextareaDiv,
  AreaLabel,
  CheckLabel,
  Checkbox,
  IncidentTypeRow,
  SignatureNote,
  Footer,
  FooterEmail,
  FooterLink
} from './views'
// types
import { ReportComponentProps } from '../../reports'
import { IncidentReportForm, INCIDENT_REPORT_FORM_DEFAULTS } from './types'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'

const IncidentReport = ({ initialValues }: ReportComponentProps) => {
  const { company } = useCurrentUser()

  const form: IncidentReportForm = {
    ...INCIDENT_REPORT_FORM_DEFAULTS,
    serviceProvider: company?.name ?? '',
    ...(initialValues as Partial<IncidentReportForm>)
  }

  return (
    <>
      <Sheet>
        <Title>Incident Report</Title>
        <Hint>
          Before completing this form, please refer to the <strong>Incident Reporting Procedure</strong>
        </Hint>

        <SectionLabel>Service Provider Details</SectionLabel>
        <Table>
          <tbody>
            <tr>
              <Td w='160px'>Service Provider:</Td>
              <Td>
                <Input as='div'>{form.serviceProvider}</Input>
              </Td>
            </tr>
            <tr>
              <Td>Program:</Td>
              <Td>
                <Input as='div'>{form.program}</Input>
              </Td>
            </tr>
          </tbody>
        </Table>

        <SectionLabel>Incident Details</SectionLabel>
        <Table>
          <tbody>
            <tr>
              <Td colSpan={2}>
                <FieldRow>
                  <FieldLabel>Location:</FieldLabel>
                  <InlineInput as='span'>{form.location}</InlineInput>
                </FieldRow>
              </Td>
            </tr>
            <tr>
              <Td>
                <FieldRow>
                  <FieldLabel>Date:</FieldLabel>
                  <InlineInput as='span'>{form.date}</InlineInput>
                </FieldRow>
              </Td>
              <Td>
                <FieldRow>
                  <FieldLabel>Time:</FieldLabel>
                  <InlineInput as='span'>{form.time}</InlineInput>
                </FieldRow>
              </Td>
            </tr>
            <tr>
              <Td>
                <FieldRow>
                  <FieldLabel>Client Name:</FieldLabel>
                  <InlineInput as='span'>{form.clientName}</InlineInput>
                </FieldRow>
              </Td>
              <Td>
                <FieldRow>
                  <FieldLabel>Client DOB:</FieldLabel>
                  <InlineInput as='span'>{form.clientDob}</InlineInput>
                </FieldRow>
              </Td>
            </tr>
            <tr>
              <Td colSpan={2}>
                <IncidentTypeRow>
                  <span>Incident Type:</span>
                  <CheckLabel>
                    <Checkbox>
                      {form.isCritical && (
                        <svg viewBox='0 0 11 11' xmlns='http://www.w3.org/2000/svg' style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                          <line x1='2' y1='2' x2='9' y2='9' stroke='#111' strokeWidth='1.5' strokeLinecap='round' />
                          <line x1='9' y1='2' x2='2' y2='9' stroke='#111' strokeWidth='1.5' strokeLinecap='round' />
                        </svg>
                      )}
                    </Checkbox>
                    Critical
                  </CheckLabel>
                  <CheckLabel>
                    <Checkbox>
                      {form.isNonCritical && (
                        <svg viewBox='0 0 11 11' xmlns='http://www.w3.org/2000/svg' style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                          <line x1='2' y1='2' x2='9' y2='9' stroke='#111' strokeWidth='1.5' strokeLinecap='round' />
                          <line x1='9' y1='2' x2='2' y2='9' stroke='#111' strokeWidth='1.5' strokeLinecap='round' />
                        </svg>
                      )}
                    </Checkbox>
                    Non-Critical
                  </CheckLabel>
                </IncidentTypeRow>
              </Td>
            </tr>
            <tr>
              <TdArea colSpan={2}>
                <AreaLabel>Details of Incident:</AreaLabel>
                <TextareaDiv minh='130px'>{form.details}</TextareaDiv>
              </TdArea>
            </tr>
            <tr>
              <TdArea colSpan={2}>
                <AreaLabel>Action Taken:</AreaLabel>
                <TextareaDiv minh='70px'>{form.actionTaken}</TextareaDiv>
              </TdArea>
            </tr>
          </tbody>
        </Table>

        <SignatureNote>This information is true to the best of my knowledge and belief:</SignatureNote>
        <Table>
          <thead>
            <tr>
              <TheadTd>Employee Details</TheadTd>
              <TheadTd>Manager Authorisation</TheadTd>
            </tr>
          </thead>
          <tbody>
            {[
              ['Name', 'employeeName', 'managerName'],
              ['Position', 'employeePosition', 'managerPosition'],
              ['Signature', 'employeeSignature', 'managerSignature'],
              ['Date', 'employeeDate', 'managerDate']
            ].map(([label, eKey, mKey]) => (
              <tr key={label}>
                <Td>
                  <FieldRow>
                    <FieldLabel>{label}:</FieldLabel>
                    <InlineInput as='span'>{form[eKey]}</InlineInput>
                  </FieldRow>
                </Td>
                <Td>
                  <FieldRow>
                    <FieldLabel>{label}:</FieldLabel>
                    <InlineInput as='span'>{form[mKey]}</InlineInput>
                  </FieldRow>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Footer>
          <p>
            For further information refer to the <strong>Incident Reporting Procedure</strong>
          </p>
          <FooterEmail>
            <strong>Email completed form to</strong>{' '}
            <FooterLink href='mailto:rrcontractedservices@justice.wa.gov.au'>
              rrcontractedservices@justice.wa.gov.au
            </FooterLink>{' '}
            and Specialist Service Unit if applicable
          </FooterEmail>
        </Footer>
      </Sheet>
    </>
  )
}

export default IncidentReport
